<template>
  <b-row>
    <b-col md="8">
      <b-card>
        <h2 class="mb-1">
          Nuevo ticket
        </h2>

        <b-form @submit.prevent="onSubmit({ certifyWithAi: false })">
          <b-form-group label="Título">
            <b-form-input
              v-model.trim="form.title"
              required
            />
          </b-form-group>

          <b-form-group label="Tipo">
            <b-form-select
              v-model="form.type"
              :options="typeOptions"
            />
          </b-form-group>

          <b-form-group label="Descripción">
            <b-form-textarea
              v-model.trim="form.description"
              rows="5"
              required
            />
          </b-form-group>

          <b-form-group label="Resultado esperado">
            <b-form-textarea
              v-model.trim="form.expected_result"
              rows="3"
            />
          </b-form-group>

          <b-row>
            <b-col md="4">
              <b-form-group label="Prioridad">
                <b-form-select
                  v-model="form.priority"
                  :options="priorityOptions"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="Ubicación">
                <b-form-select
                  v-model="form.location"
                  :options="locationOptions"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="Estimación">
                <b-form-select
                  v-model="form.estimate"
                  :options="estimateOptions"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <b-form-group label="Imágenes (opcional)">
            <b-form-file
              v-model="newImages"
              multiple
              accept="image/png,image/jpeg,image/jpg,image/webp"
              placeholder="Selecciona imágenes"
              drop-placeholder="Suelta aquí las imágenes"
            />
            <small class="text-muted d-block mt-50">
              Puedes anexar capturas del problema o mockups.
            </small>
          </b-form-group>

          <div class="d-flex flex-wrap">
            <b-button
              type="submit"
              variant="primary"
              :disabled="loading"
            >
              Crear ticket
            </b-button>

            <b-button
              class="ml-1"
              variant="success"
              :disabled="loading"
              @click="onSubmit({ certifyWithAi: true })"
            >
              Crear y certificar con IA
            </b-button>

            <b-button
              class="ml-1"
              variant="outline-secondary"
              :to="{ name: 'tickets' }"
            >
              Cancelar
            </b-button>
          </div>
        </b-form>
      </b-card>
    </b-col>
  </b-row>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  data() {
    return {
      form: {
        title: '',
        type: 'new_feature',
        description: '',
        expected_result: '',
        priority: 'medium',
        location: 'admin_panel',
        status: 'backlog',
        estimate: null,
      },
      newImages: [],
      typeOptions: [
        { value: 'bug', text: 'Bug' },
        { value: 'improvement', text: 'Mejora' },
        { value: 'new_feature', text: 'Nueva funcionalidad' },
        { value: 'ui_change', text: 'Cambio UI' },
        { value: 'performance', text: 'Performance' },
        { value: 'other', text: 'Otro' },
      ],
      priorityOptions: [
        { value: 'low', text: 'Baja' },
        { value: 'medium', text: 'Media' },
        { value: 'high', text: 'Alta' },
        { value: 'urgent', text: 'Urgente' },
      ],
      locationOptions: [
        { value: 'admin_panel', text: 'Admin panel' },
        { value: 'app', text: 'App' },
        { value: 'api', text: 'API' },
        { value: 'database', text: 'Base de datos' },
      ],
      estimateOptions: [
        { value: null, text: 'Sin estimación' },
        { value: 'l', text: 'L (1 día)' },
        { value: 'xl', text: 'XL (2-3 días)' },
        { value: 'xxl', text: 'XXL (4-5 días)' },
        { value: 'xxxl', text: 'XXXL (1-2 semanas)' },
        { value: 'epic', text: 'EPIC (2+ semanas)' },
      ],
    }
  },
  computed: {
    ...mapGetters('tickets', ['loading']),
  },
  methods: {
    ...mapActions('tickets', ['createTicket', 'refineTicketWithAi']),
    normalizeImagesInput(value) {
      if (!value) return []
      if (Array.isArray(value)) return value
      if (typeof value.length === 'number') return Array.from(value)
      return []
    },
    async onSubmit({ certifyWithAi = false } = {}) {
      try {
        const payload = await this.createTicket({
          ...this.form,
          images: this.normalizeImagesInput(this.newImages),
        })
        const created = payload?.data || payload

        this.$toast({
          component: ToastificationContent,
          props: {
            title: certifyWithAi ? 'Ticket creado, iniciando certificación IA' : 'Ticket creado',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })

        if (certifyWithAi && created?.id) {
          try {
            await this.refineTicketWithAi(created.id)
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Ticket certificado con IA',
                icon: 'CpuIcon',
                variant: 'success',
              },
            })
          } catch (certError) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: certError?.response?.data?.messages?.[0] || 'Ticket creado, pero falló la certificación IA',
                icon: 'AlertTriangleIcon',
                variant: 'warning',
              },
            })
          }
        }

        if (created?.id) {
          this.$router.push({ name: 'ticketDetail', params: { ticketId: created.id } })
        } else {
          this.$router.push({ name: 'tickets' })
        }
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error?.response?.data?.messages?.[0] || 'No se pudo crear el ticket',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },
  },
}
</script>
